import { useGlobalContext } from "@hooks/index";
import useI18n from "@i18n";
import { Box, Stack, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import BybButtonVR from "./buttons/byb-button/byb-button-vr";

const INIT_TIMEOUT_MS = 15_000;

interface IInitializationProgress {
  initialized: boolean;
}

const InitializationProgress = ({ initialized }: IInitializationProgress) => {
  const { i18n } = useI18n();
  const { globalState } = useGlobalContext();
  const [timedOut, setTimedOut] = useState(false);
  const resolveRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (initialized) {
      setTimedOut(false);
      resolveRef.current?.();
      resolveRef.current = null;
      return;
    }

    const loadPromise = new Promise<void>((resolve) => {
      resolveRef.current = resolve;
    });
    globalState.loading.events.enableLoader.emit(loadPromise);

    const timeout = setTimeout(() => {
      globalState.loading.events.resetLoader.emit();
      setTimedOut(true);
    }, INIT_TIMEOUT_MS);

    return () => clearTimeout(timeout);
  }, [initialized]);

  const handleRetry = () => window.location.reload();

  if (initialized || !timedOut) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1200,
      }}
    >
      <Stack spacing={2} sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, maxWidth: 400, textAlign: "center" }}>
        <Typography variant="text3xl700" color="dsBase.v200">
          {i18n("init.timeout.title")}
        </Typography>
        <Typography variant="text1xl500" color="dsNeutral.v300" sx={{ mb: 1 }}>
          {i18n("init.timeout.message")}
        </Typography>
        <BybButtonVR color="primary" sx={{ mt: 1 }} onClick={handleRetry}>
          {i18n("retry")}
        </BybButtonVR>
      </Stack>
    </Box>
  );
};

export default InitializationProgress;
